import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUpload, FiCheckCircle } from 'react-icons/fi';

const documentRequirements = {
  LawFirmAdmin: [
    {
      id: 1,
      name: 'Firm Registration Certificate',
      description: 'Proof of your law firm’s legal registration under local authority.',
      required: true,
    },
    {
      id: 2,
      name: 'Tax Identification Number (TIN)',
      description: 'Official tax document needed for billing and invoicing compliance.',
      required: true,
    },
    {
      id: 3,
      name: 'Owner ID Proof',
      description: 'Identity document of the law firm’s authorized signatory.',
      required: true,
    },
    {
      id: 4,
      name: 'Bank Account Proof',
      description: 'Recent statement or canceled cheque for account verification.',
      required: false,
    },
  ],
  Employee: [
    {
      id: 1,
      name: 'Bar Council ID',
      description: 'Proof that you are a certified legal practitioner.',
      required: true,
    },
    {
      id: 2,
      name: 'Government Issued ID',
      description: 'Aadhar, PAN, or other official government-issued document.',
      required: true,
    },
    {
      id: 3,
      name: 'Proof of Employment',
      description: 'Offer letter or HR-issued verification document.',
      required: false,
    },
  ],
};

interface Props {
  userRole: 'LawFirmAdmin' | 'Employee';
}

const DocumentUploadRequirements: React.FC<Props> = ({ userRole }) => {
  const docs = documentRequirements[userRole] || [];
  const [uploaded, setUploaded] = useState<Record<number, boolean>>({});
  const {t} = useTranslation()

  const handleUpload = (docId: number) => {
    setTimeout(() => {
      setUploaded(prev => ({ ...prev, [docId]: true }));
    }, 800);
  };

  return (
    <div className="flex-1 min-h-0 overflow-auto relative px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-1">{t("Document Upload")}</h1>
      <p className="text-gray-500 mb-6 text-sm">
        {t("Please upload the following documents to complete onboarding as a")} <strong>{userRole}</strong>.
      </p>

      <div className="space-y-5">
        {docs.map(doc => (
          <div
            key={doc.id}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white border border-gray-200 rounded-lg px-5 py-4 shadow-sm hover:shadow-md transition"
          >
            <div className="mb-3 sm:mb-0">
              <p className="text-base font-semibold text-gray-800">{doc.name}</p>
              <p className="text-sm text-gray-500">{doc.description}</p>
              <span
                className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                  doc.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {doc.required ? t('Required') : t('Optional')}
              </span>
            </div>

            {uploaded[doc.id] ? (
              <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
                <FiCheckCircle size={20} />
                {t("Uploaded")}
              </div>
            ) : (
              <button
                onClick={() => handleUpload(doc.id)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 hover:bg-blue-700 text-white rounded-md"
              >
                <FiUpload size={16} />
                {t("Upload")}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentUploadRequirements;
